import "@radix-ui/themes/styles.css";
import { Theme } from "@radix-ui/themes";
import type React from "react";
import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import ForgotPasswordView from "./components/auth/ForgotPasswordView";
import ResetPasswordView from "./components/auth/ResetPasswordView";
import BillingResultView from "./components/BillingResultView";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SupabaseProvider } from "./contexts/SupabaseContext";
import PublicOnly from "./pages/auth/PublicOnly";
import RequireAuth from "./pages/auth/RequireAuth";
import { configureBrowserAnalytics, legacyPostHogPageviewUrl, trackPageview } from "./utils/browserAnalytics";
import { getPostHog, initPostHog } from "./utils/posthogConfig";
import { initSentry } from "./utils/sentryConfig";
import "./index.css";
import AuthPage from "./pages/AuthPage";
import ConsentPage from "./pages/ConsentPage";
import MarketingConsentPage from "./pages/MarketingConsentPage";

// Initialize Sentry as early as possible
initSentry();

// Independent browser telemetry is configured even if PostHog is disabled.
configureBrowserAnalytics(import.meta.env.VITE_REMOTE_PRODUCT_TELEMETRY_ENABLED === "true");
// Initialize optional PostHog destination.
initPostHog();

// Setup pageview tracking
function AuthRoutes() {
    const location = useLocation();

    useEffect(() => {
        // Track pageview on mount and subsequent route changes
        const event = trackPageview(); // Independent BigQuery emission.
        // Preserve the original PostHog SPA pageview capture path and URL.
        getPostHog()?.capture("$pageview", {
            $current_url: legacyPostHogPageviewUrl(window.location),
            ...(event
                ? {
                      $insert_id: event.event_id,
                  }
                : {}),
        });
    }, [
        location,
    ]);

    return (
        <Routes>
            <Route path="/" element={<AuthPage />} />
            <Route path="/approve" element={<ConsentPage />} />
            <Route path="/payment-success" element={<BillingResultView success />} />
            <Route path="/payment-cancelled" element={<BillingResultView success={false} />} />
            <Route
                path="/welcome"
                element={
                    <RequireAuth>
                        <MarketingConsentPage />
                    </RequireAuth>
                }
            />
            {/* <Route path="/callback" element={<McpConnectorFlow />} /> */}
            <Route
                path="/forgot-password"
                element={
                    <PublicOnly>
                        <ForgotPasswordView
                            onBackToLogin={() => {
                                window.location.href = "/";
                            }}
                        />
                    </PublicOnly>
                }
            />
            <Route path="/reset-password" element={<ResetPasswordView />} />
        </Routes>
    );
}

function AuthenticatedSupabaseProvider({ children }: { children: React.ReactNode }) {
    // Bridge AuthContext's session into SupabaseProvider so Realtime can
    // subscribe to RLS-protected tables (oauth_sessions) on this auth page.
    const { user, accessToken } = useAuth();
    return (
        <SupabaseProvider accessToken={accessToken} userId={user?.id} userEmail={user?.email}>
            {children}
        </SupabaseProvider>
    );
}

function AuthApp() {
    return (
        <ErrorBoundary>
            <BrowserRouter>
                <AuthProvider>
                    <AuthenticatedSupabaseProvider>
                        <Theme appearance="dark" accentColor="blue" grayColor="slate" hasBackground={false} radius="large" scaling="95%">
                            <AuthRoutes />
                        </Theme>
                    </AuthenticatedSupabaseProvider>
                </AuthProvider>
            </BrowserRouter>
        </ErrorBoundary>
    );
}

const root = document.getElementById("root");
if (root) {
    createRoot(root).render(
        <StrictMode>
            <AuthApp />
        </StrictMode>,
    );
}
